'use client';

import { useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { clientLogger } from '@/lib/monitoring/client-logger';
import {
  ErrorCode,
  ErrorCodeType,
  isActionError,
  getErrorMessage
} from '@/lib/errors';

export interface ErrorState {
  hasError: boolean;
  error: Error | null;
  errorMessage: string;
  errorCode?: ErrorCodeType;
  isValidationWarning?: boolean;
}

export interface UseErrorHandlerReturn {
  errorState: ErrorState;
  handleError: (error: unknown, context?: string) => void;
  clearError: () => void;
  handleMutationError: (error: unknown, context?: string) => void;
  handleQueryError: (error: unknown, context?: string) => void;
  isError: boolean;
}

export interface UseErrorHandlerOptions {
  onError?: (error: Error, context?: string) => void;
}

export function useErrorHandler(options?: UseErrorHandlerOptions): UseErrorHandlerReturn {
  const onError = options?.onError;
  const [errorState, setErrorState] = useState<ErrorState>({
    hasError: false,
    error: null,
    errorMessage: '',
    isValidationWarning: false,
  });

  const queryClient = useQueryClient();
  const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearError = useCallback(() => {
    setErrorState({
      hasError: false,
      error: null,
      errorMessage: '',
      isValidationWarning: false,
    });

    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }
  }, []);

  const handleError = useCallback((error: unknown, context?: string) => {
    // Clear any existing error timeout
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
    }

    let errorCode: ErrorCodeType | undefined;
    let errorMessage: string;
    let errorObj: Error;

    // Primary path: new nested ActionError structure (from server actions using createActionError)
    if (isActionError(error)) {
      errorCode = error.error.code;
      // Use custom message if provided, otherwise fall back to centralized message
      errorMessage = error.error.message ?? getErrorMessage(error.error.code);
      errorObj = new Error(errorMessage);

    } else if (error instanceof Error) {
      errorObj = error;
      errorMessage = error.message;

      // Fallback: map infrastructure-level error messages (network, auth, DB leaks) to codes
      errorCode = mapMessageToCode(errorMessage);
      if (errorCode) {
        errorMessage = getErrorMessage(errorCode);
      }
    } else {
      errorObj = new Error(String(error));
      errorMessage = String(error);
    }

    // Determine if this is a validation/business logic warning
    const validationCodes = new Set<ErrorCodeType>([
      ErrorCode.VALIDATION_FAILED,
      ErrorCode.INVALID_INPUT,
      ErrorCode.DUPLICATE_RECORD,
      ErrorCode.INVALID_CREDENTIALS,
      ErrorCode.BRAND_ACCESS_DENIED,
      ErrorCode.EMAIL_TAKEN,
      ErrorCode.PHONE_TAKEN,
      ErrorCode.SUBSCRIPTION_EXISTS,
      ErrorCode.TRIAL_ALREADY_USED,
      ErrorCode.USERNAME_TAKEN,
      ErrorCode.M3U_URL_REQUIRED,
    ]);
    const isValidationWarning = errorCode !== undefined && validationCodes.has(errorCode);

    // Log error using client logger
    const logMessage = `Error${context ? ` in ${context}` : ''}`;
    const errorType = isValidationWarning ? 'validation_warning' : 'client_error';
    // The message can embed customer data, so only the error name and the call
    // frames are logged.
    const stackFrames = errorStackFrames(errorObj);
    const logMetadata = {
      context: context || 'unknown',
      errorCode: errorCode || 'UNMAPPED',
      errorType,
      errorName: errorObj?.name,
      ...(stackFrames ? { stackFrames } : {}),
    };

    if (isValidationWarning) {
      clientLogger.warn('system', logMessage, logMetadata, undefined).catch(console.error);
    } else {
      // The Error itself stays out: clientLogger would log its message.
      clientLogger.error('system', logMessage, logMetadata, undefined).catch(console.error);
    }

    setErrorState({
      hasError: true,
      error: errorObj,
      errorMessage,
      errorCode,
      isValidationWarning,
    });

    // Call custom error handler if provided
    if (onError) {
      onError(errorObj, context);
    }

    // Auto-clear error after 10 seconds
    errorTimeoutRef.current = setTimeout(() => {
      clearError();
    }, 10000);
  }, [onError, clearError]);

  const handleMutationError = useCallback((error: unknown, context?: string) => {
    handleError(error, context);

    // Invalidate relevant queries on mutation error
    if (context) {
      const queryKey = context.toLowerCase().replace(/\s+/g, '-');
      queryClient.invalidateQueries({ queryKey: [queryKey], exact: false });
    }
  }, [handleError, queryClient]);

  const handleQueryError = useCallback((error: unknown, context?: string) => {
    handleError(error, context);
  }, [handleError]);

  return {
    errorState,
    handleError,
    clearError,
    handleMutationError,
    handleQueryError,
    isError: errorState.hasError,
  };
}

/**
 * Maps error message patterns to error codes for errors not covered by
 * the typed ActionResult system.
 * 
 * All server actions now return typed ActionResult, so this only covers:
 * 1. Browser/network-level errors (fetch failures, timeouts)
 * 2. Auth errors from middleware or auth library (before reaching server actions)
 * 3. Prisma/database errors that may leak past server action catch blocks (safety net)
 */
function mapMessageToCode(message: string): ErrorCodeType | undefined {
  const lowerMessage = message.toLowerCase();

  // ── Network / fetch errors (browser-level, not from server actions) ──
  if (lowerMessage.includes('failed to fetch') || lowerMessage.includes('network error')) {
    return ErrorCode.NETWORK_ERROR;
  }
  if (lowerMessage.includes('timeout') || lowerMessage.includes('aborted')) {
    return ErrorCode.TIMEOUT;
  }

  // ── Auth errors (middleware / auth library, before reaching server actions) ──
  if (lowerMessage.includes('unauthorized') || lowerMessage.includes('401')) {
    return ErrorCode.UNAUTHORIZED;
  }
  if (lowerMessage.includes('forbidden') || lowerMessage.includes('403')) {
    return ErrorCode.FORBIDDEN;
  }
  if (lowerMessage.includes('session expired')) {
    return ErrorCode.SESSION_EXPIRED;
  }
  if (lowerMessage.includes('invalid email or password') ||
    lowerMessage.includes('invalid credentials') ||
    lowerMessage.includes('authentication failed')) {
    return ErrorCode.INVALID_CREDENTIALS;
  }
  if (lowerMessage.includes('account is inactive')) {
    return ErrorCode.ACCOUNT_INACTIVE;
  }

  // ── Prisma / database errors (safety net for uncaught DB errors) ──
  const alreadyUsedMessage = lowerMessage.includes('already exists') ||
    lowerMessage.includes('already taken') ||
    lowerMessage.includes('taken by another');

  if (lowerMessage.includes('username') &&
    alreadyUsedMessage) {
    return ErrorCode.USERNAME_TAKEN;
  }
  if (lowerMessage.includes('email') && alreadyUsedMessage) {
    return ErrorCode.EMAIL_TAKEN;
  }
  if (lowerMessage.includes('phone') && alreadyUsedMessage) {
    return ErrorCode.PHONE_TAKEN;
  }

  if (lowerMessage.includes('unique constraint')) {
    if (lowerMessage.includes('username')) return ErrorCode.USERNAME_TAKEN;
    if (lowerMessage.includes('email')) return ErrorCode.EMAIL_TAKEN;
    if (lowerMessage.includes('phone')) return ErrorCode.PHONE_TAKEN;
    return ErrorCode.DUPLICATE_RECORD;
  }
  if (lowerMessage.includes('foreign key constraint')) {
    return ErrorCode.FOREIGN_KEY_CONSTRAINT;
  }
  if (lowerMessage.includes('record to update not found') ||
    lowerMessage.includes('record to delete does not exist')) {
    return ErrorCode.NOT_FOUND;
  }

  // No mapping found - return undefined to use original message
  return undefined;
}

// Specialized hooks for common use cases
export function useMutationErrorHandler(context?: string) {
  const { handleMutationError, errorState, clearError } = useErrorHandler();

  return {
    handleError: (error: unknown) => handleMutationError(error, context),
    errorState,
    clearError,
  };
}

export function useQueryErrorHandler(context?: string) {
  const { handleQueryError, errorState, clearError } = useErrorHandler();
  const handleError = useCallback(
    (error: unknown) => handleQueryError(error, context),
    [handleQueryError, context],
  );

  return {
    handleError,
    errorState,
    clearError,
  };
}

// Hook for form-specific error handling
export function useFormErrorHandler() {
  const { handleError, errorState, clearError } = useErrorHandler();

  return {
    handleError,
    errorState,
    clearError,
    setFieldError: (fieldName: string, message: string) => {
      handleError(new Error(`${fieldName}: ${message}`), 'Form Validation');
    },
  };
}

// Re-export error utilities for convenience
export { ErrorCode, ErrorMessages, getErrorMessage } from '@/lib/errors';

// A frame names a script location: a URL or path ending in `:line:col`. V8 writes
// `    at fn (url:1:2)` or `    at url:1:2`; Firefox and Safari write `fn@url:1:2`.
const v8Frame = /^\s+at (?:async )?(?:.* \()?(?:[a-z][\w+.-]*:\/\/|\/|<anonymous>|node:)\S*:\d+:\d+\)?$/i;
const atFrame = /^[^@\n]*@[a-z][\w+.-]*:\/\/[^\s]*:\d+:\d+$/i;

/**
 * The call frames of an error's stack. Only lines shaped like a frame are kept,
 * so ordinary message text is dropped even when the message was changed after
 * the stack was captured; a message line deliberately written as a frame would
 * pass, and is still value-scrubbed by the logger.
 */
export function errorStackFrames(error: Error | undefined): string | undefined {
  let stack: unknown;
  try { stack = error?.stack; } catch { return undefined; }
  if (typeof stack !== 'string') return undefined;
  const lines = stack.split('\n');
  // A V8 stack starts with its message, so `fn@url` shapes are only trusted in
  // Firefox/Safari stacks, which have no V8 frames and no message header.
  const v8 = lines.filter(line => v8Frame.test(line));
  const frames = v8.length ? v8 : lines.filter(line => atFrame.test(line));
  return frames.length ? frames.join('\n') : undefined;
}
